import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { calcDirtyTabs, calcInvalidTabs } from '../../common/utils';
import { isUserPermitted } from '../../../utils';
import { ROLES } from '@silverfort-nodelibs/common/utils/consts';
import { useIsCloudOnly } from '../../hooks/featureFlags/useIsCloudOnly';

import BottomBar from '../BottomBar/BottomBar';
import Button from '../Controls/Button/Button';
import Tabs from '../Tabs/Tabs';
import UnsavedPrompt from '../UnsavedPrompt/UnsavedPrompt';
import Wizard from '../../containers/Wizard/Wizard';

import styles from './Category.module.css';

const wizardButtonStyle = {
    color: '#ffffff',
    backgroundColor: '#188ded',
    padding: '8px 16px'
};

const Category = ({
    category,
    fields,
    visibleFields,
    selectedTab,
    setSelectedTab,
    saveSettings,
    saveWizard,
    children,
    isFiltered,
    setShowFilterModal,
    tabButton,
    actions,
    actionsError,
    disableSave,
    discardAll,
    discard,
    showPerTabButtons = true
}) => {
    const firstTimeOnSettings = useSelector((state) => state.settings.settings.FirstTimeOnSettings);
    const user = useSelector((state) => state.userData.user);
    const isEditor = isUserPermitted(user, 'settings', ROLES.EDITOR.value);
    const isCloudOnly = useIsCloudOnly();

    const [showWizard, setShowWizard] = useState((false || firstTimeOnSettings) && isEditor);
    const dirtyTabs = calcDirtyTabs(fields);
    const invalidTabs = calcInvalidTabs(fields);

    const [saveError, setSaveError] = useState('');
    const [saveSuccess, setSaveSuccess] = useState('');
    const [isDuringSave, setIsDuringSave] = useState(false);
    const [saveAllDisabled, setSaveAllDisabled] = useState(
        dirtyTabs.length === 0 || invalidTabs.length > 0 || disableSave || isDuringSave
    );

    const onSaveClicked = useCallback(
        async (isSaveAll) => {
            let result;
            setIsDuringSave(true);
            try {
                result = await saveSettings(isSaveAll);
                setSaveSuccess('Saved!');
                setTimeout(() => setSaveSuccess(''), 3000);
            } catch (err) {
                setSaveError(err);
                setTimeout(() => setSaveError(''), 3000);
            } finally {
                setTimeout(() => setIsDuringSave(false), 3000);
            }
            return result;
        },
        [saveSettings]
    );

    useEffect(() => {
        setSaveAllDisabled(dirtyTabs.length === 0 || invalidTabs.length > 0 || disableSave || isDuringSave);
    }, [dirtyTabs.length, invalidTabs.length, disableSave, isDuringSave]);

    const saveAll = useCallback(() => {
        if (saveAllDisabled) {
            return;
        }
        onSaveClicked(true);
    }, [saveAllDisabled, onSaveClicked]);

    useEffect(() => {
        const categoryContent = document.getElementsByClassName(styles.content);
        if (categoryContent && categoryContent.length > 0) {
            categoryContent[0].scrollTop = 0;
        }
    }, [selectedTab]);

    const toggleWizard = useCallback(() => {
        setShowWizard(!showWizard);
    }, [showWizard, setShowWizard]);

    return (
        <>
            <UnsavedPrompt isDirty={dirtyTabs.length > 0} />
            <div className={styles.categoryHeader}>
                <div className={styles.categoryHeaderContainer}>
                    <h1 className={styles.categoryName}>{category.NAME}</h1>
                    {saveWizard && isEditor && !isCloudOnly && (
                        <Button style={wizardButtonStyle} onClick={toggleWizard}>
                            <span className={styles.buttonText}>Start Wizard</span>
                            <i className="fa fa-magic" />
                        </Button>
                    )}
                </div>

                <Tabs
                    category={category}
                    fields={fields}
                    visibleFields={visibleFields}
                    selectedTab={selectedTab}
                    setSelectedTab={setSelectedTab}
                    dirtyTabs={dirtyTabs}
                    invalidTabs={invalidTabs}
                    isFiltered={isFiltered}
                    setShowFilterModal={setShowFilterModal}
                    tabButton={tabButton}
                />
            </div>
            {children && (
                <>
                    <div className={styles.content}>{children}</div>
                    <BottomBar
                        discardAllDisabled={dirtyTabs.length < 1}
                        saveAllDisabled={saveAllDisabled}
                        actions={actions}
                        actionsError={actionsError}
                        discardAll={discardAll}
                        saveAll={saveAll}
                        saveError={saveError}
                        saveSuccess={saveSuccess}
                        discardTab={discard}
                        discardTabDisabled={!dirtyTabs.includes(selectedTab)}
                        saveTab={() => onSaveClicked(false)}
                        saveTabDisabled={
                            !dirtyTabs.includes(selectedTab) ||
                            invalidTabs.includes(selectedTab) ||
                            disableSave ||
                            isDuringSave
                        }
                        showPerTabButtons={showPerTabButtons}
                    />
                </>
            )}
            {showWizard && !isCloudOnly && <Wizard saveFunc={saveWizard} onHide={toggleWizard} />}
        </>
    );
};

export default Category;
